import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
} from '@angular/core';
import { MaterialModule } from '../../shared/material/material.module';
import {
  ProfileProviderPF,
  ProfileProviderPfControllerService,
  ProfileProviderPJ,
  ProfileProviderPjControllerService,
  UserRef,
} from '../../core/generated';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { ProfileProviderPFStateService } from '../../shared/services/profile-provider-pf-state.service';
import { ProfileProviderPJStateService } from '../../shared/services/profile-provider-pj-state.service';
import { PlanStatusService } from '../../shared/services/plan-status-cache.service';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
import { ContainerComponent } from '../../shared/container/container.component';

@Component({
    selector: 'app-profiles',
    standalone: true,
    imports: [
        MaterialModule,
        RouterOutlet,
        EnumDescriptionPipe,
        ContainerComponent
    ],
    templateUrl: './profiles.component.html',
    styleUrl: './profiles.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ProfilesComponent implements OnInit {
  private profileProviderPFService = inject(ProfileProviderPfControllerService);
  private profileProviderPJService = inject(ProfileProviderPjControllerService);
  private profileProviderPFStateService = inject(ProfileProviderPFStateService);
  private profileProviderPJStateService = inject(ProfileProviderPJStateService);
  private planStatusService = inject(PlanStatusService);
  private _snackBar = inject(MatSnackBar);
  private UserPreferencesCacheService = inject(UserPreferencesCacheService);
  public router = inject(Router);
  deviceService = inject(DeviceDetectorService);
  private route = inject(ActivatedRoute);

  isLoading = false;
  user!: UserRef;
  profileProviderPF: ProfileProviderPF[] = [];
  profileProviderPJ: ProfileProviderPJ[] = [];
  isMobile = this.deviceService.isMobile();

  displayedColumnsProviderPF: string[] = [
    'certifiedHubMedical',
    'name',
    'registerDate',
    'active',
    'status',
    'actions',
  ];

  displayedColumnsProviderPJ: string[] = [
    'certifiedHubMedical',
    'companyName',
    'registerDate',
    'active',
    'status',
    'actions',
  ];

  async ngOnInit() {
    this.user = await this.getUser();

    if (this.isMobile) {
      this.adjustColumnsForMobile();
    }

    this.isLoading = true;
    await Promise.all([
      this.loadProviderPF(),
      this.loadProviderPJ(),
    ]);
    this.isLoading = false;

    let hasPackageType = false;
    this.route.queryParams.subscribe(params => {
      if (params['packageType']) {
        hasPackageType = params['packageType'];

      }
    });
    if (
      this.profileProviderPF.length < 1 &&
      this.profileProviderPJ.length < 1 && !hasPackageType
    ) {
      this.router.navigate(['/my-account/profiles/select-new-profile']);
    }
  }

  private async getUser(): Promise<UserRef> {
    return new Promise((resolve, reject) => {
      this.UserPreferencesCacheService.getUser().subscribe(
        (user) => resolve(user),
        (error) => reject(error)
      );
    });
  }

  private adjustColumnsForMobile() {
    this.displayedColumnsProviderPF = [
      'certifiedHubMedical',
      'name',
      'status',
      'actions',
    ];
    this.displayedColumnsProviderPJ = [
      'certifiedHubMedical',
      'companyName',
      'status',
      'actions',
    ];
  }

  private async loadProviderPF() {
    if (this.user?.email) {
      const profile = await this.profileProviderPFService
        .findByUserEmail1(this.user.email)
        .toPromise();
      // Só adiciona ao array se o perfil tem ID válido
      if (profile && profile.id) {
        this.profileProviderPF.push(profile);
      }
    }
  }

  private async loadProviderPJ() {
    if (this.user?.email) {
      const profiles = await this.profileProviderPJService
        .findByUserEmail(this.user.email)
        .toPromise();
      // Filtra apenas perfis válidos com ID
      if (profiles && Array.isArray(profiles)) {
        this.profileProviderPJ = profiles.filter(p => p && p.id);
      }
    }
  }

  removeProviderPF(id: number) {
    this.profileProviderPFService.deleteById6(id).subscribe(() => {
      this.profileProviderPF = this.profileProviderPF.filter(
        (pf) => pf.id !== id
      );

      // Atualizar o estado global - remover perfil PF
      this.profileProviderPFStateService.removeProfile();

      // Limpar cache de planos do CPF removido
      if (this.user?.cpf) {
        this.planStatusService.clearCacheFor(this.user.cpf);
      }

      // Forçar atualização do PlanStatusService (atualiza sidebar e header)
      this.planStatusService.refresh().catch(err => 
        console.error('Error refreshing plan statuses after removing PF profile', err)
      );

      this._snackBar.open('Provider PF removido com sucesso.', '', {
        duration: 3000,
      });
    });
  }

  removeProviderPJ(id: number) {
    // Encontrar o CNPJ antes de remover
    const profileToRemove = this.profileProviderPJ.find(pj => pj.id === id);
    const cnpjToRemove = profileToRemove?.cnpj;

    this.profileProviderPJService.deleteById5(id).subscribe(() => {
      this.profileProviderPJ = this.profileProviderPJ.filter(
        (pj) => pj.id !== id
      );

      // Atualizar o estado global - remover perfil PJ
      this.profileProviderPJStateService.removeProfile(id);

      // Limpar cache de planos do CNPJ removido
      if (cnpjToRemove) {
        this.planStatusService.clearCacheFor(cnpjToRemove);
      }

      // Forçar atualização do PlanStatusService (atualiza sidebar e header)
      this.planStatusService.refresh().catch(err => 
        console.error('Error refreshing plan statuses after removing PJ profile', err)
      );

      this._snackBar.open('Provider PJ removido com sucesso.', '', {
        duration: 3000,
      });
    });
  }

  editProfilePF(profilePF: ProfileProviderPF) {
    this.router.navigate(
      ['my-account/profiles/profileProviderPFForm', profilePF.id],
      {
        state: { data: profilePF },
      }
    );
  }

  editProfilePJ(profilePJ: ProfileProviderPJ) {
    this.router.navigate(
      ['my-account/profiles/profileProviderPJForm', profilePJ.id],
      {
        state: { data: profilePJ },
      }
    );
  }

  isLeaderProviderPJ(id: number): boolean {
    if (!this.user) {
      return false;
    }

    return this.profileProviderPJ.some(
      (providerPJ) =>
        providerPJ.id === id &&
        providerPJ.providerPJUser?.some(
          (pjuser) => pjuser?.user?.email === this.user?.email
        )
    );
  }

}
